// btw-front/src/components/admin/dashboard/StaleDealsTab.jsx
// [관리 대상 딜] 탭 — 장기 미수정 딜 점검 + 자동 알림 설정 + 갱신 요청 안내 발송(단건/일괄)
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { message, Modal } from "antd";
import * as adminStaleDeals from "../../../api/adminStaleDeals";
import { Input } from "../../ui/Input";
import { useQueryState } from "../../../lib/useQueryState";
import { Tabs, pad2 } from "./shared";
import StaleAlertSettingCard from "./StaleAlertSettingCard";
import StaleDealsList from "./StaleDealsList";
import StaleBulkNoticeModal from "./StaleBulkNoticeModal";

// 검색어 입력 후 목록을 다시 불러오기까지 기다리는 시간 — 글자마다 재조회하지 않도록
const SEARCH_DEBOUNCE_MS = 300;
// 일괄 발송 미리보기에 넘길 수 있는 딜 id 상한 — 서버 staleDealRoutes 의 BULK_DEAL_IDS_MAX 와 동기
const BULK_DEAL_IDS_MAX = 500;

export default function StaleDealsTab() {
  const { t } = useTranslation();

  const [days, setDays] = useState(30);
  // 키워드(파트너사·고객사·영업대표) — URL 에 보관해 딜 상세에서 돌아와도 유지.
  // 목록·엑셀·일괄 발송 후보가 모두 이 검색 결과를 기준으로 한다.
  const [q, setQ] = useQueryState("q");
  // 실제 조회에 쓰는 검색어 — 입력(q)이 멈추고 SEARCH_DEBOUNCE_MS 뒤에 따라온다.
  // 처음엔 URL 의 값을 그대로 써서 상세에서 돌아올 때 지연 없이 같은 목록을 그린다.
  const [searchQ, setSearchQ] = useState(() => q.trim());
  // 입력 중(아직 조회에 반영 전) — 엑셀·일괄 발송이 화면과 다른 검색어로 실행되지 않게 막는다
  const searchPending = q.trim() !== searchQ;
  const [data, setData] = useState({ days: 30, items: [] });
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [noticeSendingId, setNoticeSendingId] = useState(null);

  const [setting, setSetting] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [form, setForm] = useState({ isEnabled: false, thresholdDays: 30, hour: 11, minute: 0 });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  // 일괄 발송 팝업 — 서버 미리보기(후보 그룹) + 팝업 안에서 체크한 딜 id 집합
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkSelected, setBulkSelected] = useState(() => new Set());
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  // 정렬 상태 — 기본: 정체 일수 오름차순
  const [sort, setSort] = useState({ key: "days", dir: "asc" });

  // 목록 로드 (기간 변경 시)
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminStaleDeals
      .list(days, searchQ)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        if (!cancelled) setData({ days, items: [] });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days, searchQ, reloadKey]);

  // 입력 디바운스 — 마지막 입력 후 SEARCH_DEBOUNCE_MS 가 지나면 조회 검색어로 확정
  useEffect(() => {
    const handle = setTimeout(() => setSearchQ(q.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [q]);

  // 자동 알림 설정 로드 (최초 1회)
  useEffect(() => {
    let cancelled = false;
    adminStaleDeals
      .getSetting()
      .then((s) => {
        if (cancelled) return;
        setSetting(s);
        const [h, m] = (s.sendAt || "11:00").split(":").map((x) => Number(x));
        setForm({ isEnabled: s.isEnabled, thresholdDays: s.thresholdDays, hour: h, minute: m });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // 상세 저장 — 정체 기준·발송 시각만. 켜짐/꺼짐은 상단 토글이 별도 관장.
  const saveSetting = () => {
    setSaving(true);
    adminStaleDeals
      .updateSetting({
        thresholdDays: Number(form.thresholdDays),
        sendAt: `${pad2(form.hour)}:${pad2(form.minute)}`,
      })
      .then((s) => {
        setSetting(s);
        const [h, m] = (s.sendAt || "11:00").split(":").map((x) => Number(x));
        setForm({ isEnabled: s.isEnabled, thresholdDays: s.thresholdDays, hour: h, minute: m });
        message.success(t("admin.staleDeals.setting.saved"));
      })
      .catch((e) => message.error((e && e.message) || t("admin.staleDeals.setting.saveFailed")))
      .finally(() => setSaving(false));
  };

  // 빠른 켜짐/꺼짐 토글 — 확장·저장 없이 즉시 반영(자동 발송 사용 여부만 갱신)
  const quickToggleEnabled = (next) => {
    setToggling(true);
    adminStaleDeals
      .updateSetting({ isEnabled: next })
      .then((s) => {
        setSetting(s);
        setForm((f) => ({ ...f, isEnabled: s.isEnabled }));
        message.success(
          s.isEnabled
            ? t("admin.staleDeals.setting.turnedOn")
            : t("admin.staleDeals.setting.turnedOff"),
        );
      })
      .catch((e) => message.error((e && e.message) || t("admin.staleDeals.setting.saveFailed")))
      .finally(() => setToggling(false));
  };

  const sendNow = () => {
    setSending(true);
    adminStaleDeals
      .sendNow()
      .then((res) => {
        if (res.status === "sent")
          message.success(
            t("admin.staleDeals.setting.sendSent", {
              n: res.dealCount,
              recipients: res.recipientCount,
            }),
          );
        else if (res.status === "skipped") message.info(t("admin.staleDeals.setting.sendSkipped"));
        else message.error(t("admin.staleDeals.setting.sendFailed"));
      })
      .catch((e) => message.error((e && e.message) || t("admin.staleDeals.setting.sendFailed")))
      .finally(() => setSending(false));
  };

  // 안내 발송 실패 공통 처리 — 코드별 안내 후 필요 시 목록 갱신
  const handleNoticeError = (e) => {
    const code = e && e.payload && e.payload.error && e.payload.error.code;
    if (code === "ALREADY_NOTICED") {
      message.info(t("admin.staleDeals.notice.toasts.alreadySent"));
      setReloadKey((k) => k + 1);
    } else if (code === "NO_RECIPIENT") {
      message.error(t("admin.staleDeals.notice.toasts.noRecipient"));
    } else {
      message.error(
        (e && e.payload && e.payload.error && e.payload.error.message) ||
          t("admin.staleDeals.notice.toasts.failed"),
      );
    }
  };

  // 딜 갱신 요청 안내 메일 — 서버 미리보기(발송과 동일 템플릿)를 다이얼로그에 표시 후,
  // '발송' 확정 시 발송. 발송 후 딜 갱신 전까지 재발송 차단.
  const sendNotice = (d) => {
    if (noticeSendingId) return;
    setNoticeSendingId(d.id);
    adminStaleDeals
      .previewNotice(d.id)
      .then((preview) => {
        Modal.confirm({
          title: t("admin.staleDeals.notice.confirmTitle"),
          width: 560,
          content: (
            <div>
              <p>
                {t("admin.staleDeals.notice.confirmRecipient")}:{" "}
                <strong>
                  {preview.recipientName || "-"} ({preview.recipientEmail})
                </strong>
              </p>
              <p className="mt-3 text-xs font-medium text-slate-500">
                {t("admin.staleDeals.notice.previewLabel")}
              </p>
              <div className="mt-1 max-h-72 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-sm font-semibold text-slate-800">{preview.subject}</p>
                <pre className="mt-2 whitespace-pre-wrap font-sans text-xs leading-relaxed text-slate-700">
                  {preview.text}
                </pre>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                {t("admin.staleDeals.notice.confirmDesc")}
              </p>
            </div>
          ),
          okText: t("admin.staleDeals.notice.confirmOk"),
          cancelText: t("admin.staleDeals.notice.confirmCancel"),
          onOk: () =>
            adminStaleDeals
              .sendNotice(d.id)
              .then(() => {
                message.success(t("admin.staleDeals.notice.toasts.sent"));
                setReloadKey((k) => k + 1);
              })
              .catch(handleNoticeError),
        });
      })
      .catch(handleNoticeError)
      .finally(() => setNoticeSendingId(null));
  };

  // 일괄 발송 1단계 — 서버 미리보기(발송 가능 후보를 수신자별 그룹으로)를 받아 팝업을 연다.
  // 팝업 안에서 딜을 체크해 발송(2단계 confirmBulkNotice). 잠금(bulkSending)은 팝업이
  // 닫힐 때까지 유지 — 툴바 버튼 재진입으로 미리보기·팝업이 중복되는 것을 방지.
  const openBulkNotice = () => {
    if (bulkSending) return;
    // 검색 중이면 화면에 보이는 딜만 후보로 — 서버가 그 id 들로 다시 계산·검증한다
    const visibleIds = searchQ ? data.items.map((d) => d.id) : undefined;
    // 서버 상한을 넘으면 요청 전에 안내 — 그대로 보내면 400 으로 '발송 실패' 처럼 보인다
    if (visibleIds && visibleIds.length > BULK_DEAL_IDS_MAX) {
      message.warning(t("admin.staleDeals.bulk.tooManyResults", { max: BULK_DEAL_IDS_MAX }));
      return;
    }
    setBulkSending(true);
    adminStaleDeals
      .previewBulkNotice(days, visibleIds)
      .then((preview) => {
        if (!preview.groups.length) {
          message.info(
            t("admin.staleDeals.bulk.nothingToSend", {
              skipped: preview.skippedAlreadyNotified,
            }),
          );
          setBulkSending(false);
          return;
        }
        // 기본값: 후보 전체 체크 — 관리자가 보낼 필요 없는 딜만 해제하는 흐름
        setBulkSelected(new Set(preview.groups.flatMap((g) => g.deals.map((d) => d.id))));
        setBulkPreview(preview);
      })
      .catch((e) => {
        message.error(
          (e && e.payload && e.payload.error && e.payload.error.message) ||
            t("admin.staleDeals.bulk.toasts.failed"),
        );
        setBulkSending(false);
      });
  };

  // 팝업 상태 초기화 — 발송 완료 직후(bulkSubmitting 아직 true)에도 호출되므로 잠금 판단 없음
  const resetBulkNotice = () => {
    setBulkPreview(null);
    setBulkSelected(new Set());
    setBulkSending(false);
  };
  // 취소/닫기 — 발송 중에는 무시. 잠금이 풀려 두 번째 팝업이 열리면 첫 요청 완료 시
  // 덮어 닫히는 문제 방지 (Modal 의 closable/keyboard/maskClosable 잠금과 이중 방어)
  const closeBulkNotice = () => {
    if (bulkSubmitting) return;
    resetBulkNotice();
  };

  const toggleBulkDeal = (id) =>
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  // 그룹(수신자) 단위 토글 — 그룹 전부 체크 상태면 해제, 아니면 전부 체크
  const toggleBulkGroup = (group) =>
    setBulkSelected((prev) => {
      const ids = group.deals.map((d) => d.id);
      const all = ids.every((id) => prev.has(id));
      const next = new Set(prev);
      ids.forEach((id) => (all ? next.delete(id) : next.add(id)));
      return next;
    });
  const bulkAllIds = bulkPreview ? bulkPreview.groups.flatMap((g) => g.deals.map((d) => d.id)) : [];
  const bulkAllSelected = bulkAllIds.length > 0 && bulkAllIds.every((id) => bulkSelected.has(id));
  const toggleBulkAll = () => setBulkSelected(bulkAllSelected ? new Set() : new Set(bulkAllIds));
  // 선택된 딜을 가진 수신자 수 (한 통씩 발송되므로 = 발송 메일 수)
  const bulkSelectedRecipients = bulkPreview
    ? bulkPreview.groups.filter((g) => g.deals.some((d) => bulkSelected.has(d.id))).length
    : 0;

  // 일괄 발송 2단계 — 팝업에서 체크한 딜만 발송. 서버가 선택 id 로 다시 계산·검증한다.
  const confirmBulkNotice = () => {
    if (bulkSubmitting || bulkSelected.size === 0) return;
    setBulkSubmitting(true);
    adminStaleDeals
      .sendBulkNotice(days, [...bulkSelected])
      .then((r) => {
        if (r.failedMails > 0) {
          message.warning(
            t("admin.staleDeals.bulk.toasts.partial", {
              mails: r.sentMails,
              failed: r.failedMails,
            }),
          );
        } else if (r.sentMails === 0) {
          // 팝업을 열어둔 사이 목록이 갱신돼 발송 대상이 사라진 경우 — 사유를 함께 안내
          const reasons = [
            t("admin.staleDeals.bulk.nothingToSend", { skipped: r.skippedAlreadyNotified }),
          ];
          if (r.skippedNotInList > 0) {
            reasons.push(t("admin.staleDeals.bulk.notInListNote", { n: r.skippedNotInList }));
          }
          message.info(reasons.join(" "));
        } else {
          message.success(
            t("admin.staleDeals.bulk.toasts.sent", { mails: r.sentMails, deals: r.sentDeals }),
          );
        }
        resetBulkNotice();
        setReloadKey((k) => k + 1);
      })
      .catch((e) =>
        message.error(
          (e && e.payload && e.payload.error && e.payload.error.message) ||
            t("admin.staleDeals.bulk.toasts.failed"),
        ),
      )
      .finally(() => setBulkSubmitting(false));
  };

  // 현재 목록(선택된 정체 기간 기준) 엑셀 다운로드
  const downloadExcel = () => {
    if (downloading) return;
    setDownloading(true);
    adminStaleDeals
      .downloadExcel(days, searchQ)
      .catch((e) => message.error(e.message || t("admin.staleDeals.excelFailed")))
      .finally(() => setDownloading(false));
  };

  // 정체 일수 강조 — 오래될수록 경고색(관리 우선순위 시각화)
  // 정렬 — 정체 일수/고객사/파트너(등록) 컬럼 헤더 클릭으로 오름↔내림 토글.
  // 같은 컬럼 재클릭 시 방향 전환, 다른 컬럼으로 바꿀 땐 오름차순으로 시작.
  const toggleSort = (key) =>
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" },
    );
  const sortedItems = useMemo(() => {
    const arr = [...data.items];
    const mul = sort.dir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      if (sort.key === "days") return (a.daysSinceUpdate - b.daysSinceUpdate) * mul;
      const av = sort.key === "company" ? a.displayCompanyName : a.customerCompany;
      const bv = sort.key === "company" ? b.displayCompanyName : b.customerCompany;
      return (av || "").localeCompare(bv || "", "ko") * mul;
    });
    return arr;
  }, [data.items, sort]);

  return (
    <div className="space-y-4">
      <StaleAlertSettingCard
        days={days}
        form={form}
        loading={loading}
        quickToggleEnabled={quickToggleEnabled}
        saveSetting={saveSetting}
        saving={saving}
        sendNow={sendNow}
        sending={sending}
        setForm={setForm}
        setSettingsOpen={setSettingsOpen}
        setting={setting}
        settingsOpen={settingsOpen}
        toggling={toggling}
      />

      {/* 정체 기간 툴바 — 이 탭의 주 조작. 목록을 위로 끌어올림 */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="text-xs font-medium text-slate-500">
          {t("admin.staleDeals.periodLabel")}
        </span>
        <Tabs
          value={String(days)}
          onChange={(v) => setDays(Number(v))}
          items={[
            { key: "3", label: t("admin.staleDeals.period.3d") },
            { key: "5", label: t("admin.staleDeals.period.5d") },
            { key: "7", label: t("admin.staleDeals.period.1w") },
            { key: "14", label: t("admin.staleDeals.period.2w") },
            { key: "21", label: t("admin.staleDeals.period.3w") },
            { key: "30", label: t("admin.staleDeals.period.1m") },
            { key: "60", label: t("admin.staleDeals.period.2m") },
            { key: "90", label: t("admin.staleDeals.period.3m") },
          ]}
        />
        <Input
          name="search"
          placeholder={t("admin.staleDeals.searchPlaceholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-60"
        />
        <button
          type="button"
          onClick={downloadExcel}
          disabled={downloading || loading || searchPending || data.items.length === 0}
          className="ml-auto rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
        >
          {downloading ? t("admin.staleDeals.excelDownloading") : t("admin.staleDeals.excel")}
        </button>
        <button
          type="button"
          onClick={openBulkNotice}
          disabled={bulkSending || loading || searchPending || data.items.length === 0}
          className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
        >
          {bulkSending && !bulkPreview
            ? t("admin.staleDeals.bulk.loading")
            : t("admin.staleDeals.bulk.button")}
        </button>
        <span className="text-xs font-medium text-slate-500">
          {t("admin.staleDeals.count", { n: data.items.length })}
        </span>
      </div>

      <StaleDealsList
        data={data}
        days={days}
        isSearching={!!searchQ}
        loading={loading}
        noticeSendingId={noticeSendingId}
        sendNotice={sendNotice}
        sort={sort}
        sortedItems={sortedItems}
        toggleSort={toggleSort}
      />

      <StaleBulkNoticeModal
        bulkAllSelected={bulkAllSelected}
        bulkPreview={bulkPreview}
        bulkSelected={bulkSelected}
        bulkSelectedRecipients={bulkSelectedRecipients}
        bulkSubmitting={bulkSubmitting}
        closeBulkNotice={closeBulkNotice}
        confirmBulkNotice={confirmBulkNotice}
        days={days}
        toggleBulkAll={toggleBulkAll}
        toggleBulkDeal={toggleBulkDeal}
        toggleBulkGroup={toggleBulkGroup}
      />
    </div>
  );
}
